import { useState, useEffect, useRef, useCallback } from "react";
import useUserId from "./useUserId";

/**
 * Custom hook for managing Carnaval WebSocket connections
 * Provides real-time updates for participant counts and queue positions
 */
const useCarnivalWebSocket = (carnivalId, userIdParam = null, options = {}) => {
  const { userId: hookUserId } = useUserId();

  // Extract actual user ID properly - handle both object and primitive inputs
  let actualUserId;
  if (userIdParam) {
    // Use provided parameter
    if (typeof userIdParam === "object" && userIdParam !== null) {
      actualUserId = userIdParam.userId || userIdParam.id || userIdParam;
    } else {
      actualUserId = userIdParam;
    }
  } else {
    // Fallback to hook
    if (typeof hookUserId === "object" && hookUserId !== null) {
      actualUserId = hookUserId.userId || hookUserId.id || hookUserId;
    } else {
      actualUserId = hookUserId;
    }
  }

  const userId = actualUserId;
  const [isConnected, setIsConnected] = useState(false);
  const [carnivalData, setCarnivalData] = useState(null);
  const [queueInfo, setQueueInfo] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const reconnectAttempts = useRef(0);

  const {
    autoReconnect = true,
    maxReconnectAttempts = 5,
    reconnectDelay = 3000,
    heartbeatInterval = 0, // Disabled by default - WebSocket connections are stable
    onParticipantUpdate,
    onQueueUpdate,
    onStatusChange,
    onError,
    debug = false,
  } = options;

  const log = useCallback(
    (message, data = null) => {
      if (debug) {
        console.log(`[CarnivalWebSocket] ${message}`, data || "");
      }
    },
    [debug]
  );

  const connect = useCallback(() => {
    if (!carnivalId || !userId) {
      log("Cannot connect: missing carnivalId or userId");
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      log("WebSocket already connected");
      return;
    }

    try {
      // Use same approach as Chat.jsx - simple and working
      const apiUrlsoket = process.env.REACT_APP_API_URL_SOKET;
      const wsUrl = `${apiUrlsoket}ws/carnival/${carnivalId}/${userId}/`;

      log("Connecting to:", wsUrl);

      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        log("WebSocket connected successfully");
        setIsConnected(true);
        setError(null);
        reconnectAttempts.current = 0;

        // Request initial data
        wsRef.current.send(JSON.stringify({ type: "request_carnival_status" }));
        wsRef.current.send(JSON.stringify({ type: "request_queue_position" }));

        // Start heartbeat
        if (heartbeatInterval > 0) {
          heartbeatIntervalRef.current = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: "ping" }));
            }
          }, heartbeatInterval);
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          log("Received message:", data);

          setLastUpdate(new Date().toISOString());

          switch (data.type) {
            case "carnival_status":
              setCarnivalData(data.carnival);
              break;

            case "queue_position":
              setQueueInfo(data.queue_info);
              break;

            case "participant_update":
              setCarnivalData((prev) =>
                prev
                  ? {
                      ...prev,
                      current_participants: data.current_participants,
                      max_participants: data.max_participants,
                    }
                  : null
              );

              if (onParticipantUpdate) {
                onParticipantUpdate(data);
              }
              break;

            case "queue_update":
              // Request updated queue position
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(
                  JSON.stringify({ type: "request_queue_position" })
                );
              }

              if (onQueueUpdate) {
                onQueueUpdate(data);
              }
              break;

            case "status_change":
              setCarnivalData((prev) =>
                prev
                  ? {
                      ...prev,
                      is_active: data.is_active,
                      is_archived: data.is_archived,
                    }
                  : null
              );

              if (onStatusChange) {
                onStatusChange(data);
              }
              break;

            case "pong":
              log("Received heartbeat pong");
              break;

            case "connection_test":
              break;

            default:
              log("Unknown message type:", data.type);
          }
        } catch (err) {
          log("Error parsing message:", err);
          setError("Failed to parse WebSocket message");
        }
      };

      wsRef.current.onclose = (event) => {
        log("WebSocket closed:", event.code, event.reason);
        setIsConnected(false);

        // Clear heartbeat
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }

        // Attempt reconnection if enabled
        if (autoReconnect && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          log(
            `Attempting reconnection ${reconnectAttempts.current}/${maxReconnectAttempts}`
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay);
        } else if (reconnectAttempts.current >= maxReconnectAttempts) {
          setError("Maximum reconnection attempts reached");
          if (onError) {
            onError("Maximum reconnection attempts reached");
          }
        }
      };

      wsRef.current.onerror = (error) => {
        log("WebSocket error:", error);
        setError("WebSocket connection error");
        if (onError) {
          onError("WebSocket connection error");
        }
      };
    } catch (err) {
      log("Error creating WebSocket:", err);
      setError("Failed to create WebSocket connection");
      if (onError) {
        onError("Failed to create WebSocket connection");
      }
    }
  }, [
    carnivalId,
    userId,
    autoReconnect,
    maxReconnectAttempts,
    reconnectDelay,
    heartbeatInterval,
    onParticipantUpdate,
    onQueueUpdate,
    onStatusChange,
    onError,
    log,
  ]);

  const disconnect = useCallback(() => {
    // Clear timeouts and intervals
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
    reconnectAttempts.current = 0;
  }, []);

  const requestCarnivalStatus = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "request_carnival_status" }));
    }
  }, []);

  const requestQueuePosition = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "request_queue_position" }));
    }
  }, []);

  // Connect when hook is initialized - stable connection
  useEffect(() => {
    if (carnivalId && userId) {
      connect();
    }

    // Only disconnect when component unmounts or IDs change
    return () => {
      disconnect();
    };
  }, [carnivalId, userId]); // Only depend on stable IDs

  return {
    isConnected,
    carnivalData,
    queueInfo,
    lastUpdate,
    error,
    connect,
    disconnect,
    requestCarnivalStatus,
    requestQueuePosition,
  };
};

export default useCarnivalWebSocket;
